import { LoaderCircle } from "lucide-react";
import { useMemo } from "react";
import type { PendingAuthenticationProps } from "@/components/bank-id/components/pending-authentication";
import { BankIdQrCode } from "@/components/bank-id/components/pending-authentication/bank-id-authentication-elements/qr-code";
import { Countdown } from "@/components/bank-id/components/pending-authentication/utilities/countdown";
import { BankIdButton } from "@/components/bank-id/components/utilities/bank-id-button";
import { useTranslations } from "@/hooks/use-translations";
import { useBankIdStore } from "@/stores/bank-id/bank-id-store";
import type { BankIdStore } from "@/stores/bank-id/bank-id-store.types";

export function BankIdAuthenticationElements({
  closeModal,
  authProcess,
}: PendingAuthenticationProps) {
  const { __ } = useTranslations();

  const {
    orderRef,
    autoStartToken,
    isDesktop,
    openOnThisDevice,
    switchDevice,
  } = useBankIdStore((state: BankIdStore) => state);

  const linkToApplication = (
    newOrderRef: string,
    newAutoStartToken: string
  ): string => {
    if (!(newOrderRef && autoStartToken)) {
      return "";
    }

    return isDesktop
      ? `bankid:///?autostarttoken=${newAutoStartToken}`
      : `https://app.bankid.com/?autostarttoken=${newAutoStartToken}`;
  };

  const buttonLabel: string = useMemo(
    () =>
      openOnThisDevice
        ? __("bank_id.open_on_other_device")
        : __("bank_id.open_on_this_device"),
    [openOnThisDevice, __]
  );

  const toggleOpenOnDevice = () => {
    if (!linkToApplication(orderRef as string, autoStartToken as string)) {
      return;
    }

    const shouldOpenOnThisDevice = !openOnThisDevice;

    switchDevice(
      shouldOpenOnThisDevice,
      authProcess,
      (newOrderRef: string, newAutoStartToken: string) => {
        if (shouldOpenOnThisDevice) {
          window.location.href = linkToApplication(
            newOrderRef as string,
            newAutoStartToken as string
          );
        }
      }
    );
  };

  return (
    <div>
      {!openOnThisDevice && <BankIdQrCode />}

      {openOnThisDevice && (
        <LoaderCircle
          className="mx-auto mb-4 h-12 w-12 animate-spin"
          stroke={"#27C327"}
        />
      )}

      <Countdown />

      <div className={"mt-4 grid grid-cols-1 gap-y-2 lg:mt-6"}>
        <BankIdButton aria-label={buttonLabel} onClick={toggleOpenOnDevice}>
          {buttonLabel}
        </BankIdButton>
        <BankIdButton onClick={closeModal} variant={"secondary"}>
          {__("bank_id.cancel")}
        </BankIdButton>
      </div>
    </div>
  );
}
